/**
 * L'onglet Rapports de la fiche d'un élève : la route `live-reports` (`LiveReportsScreen.js`, hors mode
 * parent, `studentId`), le « Diagnostic » de la ligne de Mes élèves (`StudentsListItem.js`), et le
 * rapport complet que la route `r` ouvrait, réunis dans l'onglet.
 *
 * Les mêmes lectures que l'ancien code, et pas une de plus — la table `TUTOR_STUDENT_REPORTS_EQUIVALENCE` :
 * - la liste : `getStudentLiveReports { id: élève }`, par pages (`{ page }`), à chaque ouverture de
 *   l'onglet (l'ancien écran était poussé à chaque fois), relue et prolongée comme celle du parent
 *   (`useLiveReportsData`, qui est le port de `PaginatedFlatList`) ;
 * - le diagnostic : `getStudentLiveReportsWithDiagnostic { id: élève }`, une fois par élève, gardé ensuite
 *   (l'ancienne ligne le gardait, `studentLiveReportsWithDiagnostic[id]`). Il était lu au toucher de
 *   « Diagnostic » ; il est en tête de l'onglet, donc lu à son ouverture. Un 404 est la réponse « aucun
 *   diagnostic » : pas de bloc (l'ancienne ligne affichait le message du serveur en erreur). Une autre
 *   erreur se dit dans le bloc, avec « Réessayer » (l'ancienne ne disait rien) ;
 * - un rapport touché : `getLiveReport { hash } { with_linked_assessments: 1 }` et les matières, la lecture
 *   de la route `r` (`useLiveReportDetailsData`, tel quel : un tuteur n'y déclenche ni le journal du mode
 *   parent ni le parrainage). Un rapport sans statistiques (une absence) reste inerte, comme la ligne
 *   grise de l'ancien tableau.
 *
 * Rien ne s'écrit ici : l'onglet ne mesure aucun geste.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiIdle } from "../../../../../../data/redux/actions/api"
import { liveReportService } from "../../../../../../data/services/liveReportService"
import { type TutorStudentReportsPageProps } from "../../../../../../design-system/pages/TutorStudentReportsPage"
import {
    isNoDiagnosticAnswer,
    type ApiReportWithDiagnostic,
} from "../../../../adapters/tutor/Students/tutorStudentReportsAdapter"
import { liveReportDetailsViewModel } from "../../../../view-models/parent/LiveReports/liveReportDetailsViewModel"
import {
    makeTutorStudentDiagnosticViewModel,
    makeTutorStudentReportsViewModel,
} from "../../../../view-models/tutor/Students/tutorStudentReportsViewModel"
import { useApiFetch } from "../../../hooks/useApiFetch"
import { type UILiveSessionReport } from "../../../types"
import { useLiveReportDetailsData } from "../../parent/LiveReports/useLiveReportDetailsData"
import { useLiveReportsData } from "../../parent/LiveReports/useLiveReportsData"
import { type TutorStudentTab, type TutorStudentTabArgs } from "./tutorStudentTabs"

/** Ce que l'ancien code lisait, geste par geste : chaque ligne est jouée par un test sur le crochet. */
export const TUTOR_STUDENT_REPORTS_EQUIVALENCE = {
    open_tab: {
        gesture: "Ouvrir l'onglet Rapports (l'ancien « Rapports Live » de la ligne, ou la route `live-reports`)",
        legacy: "common/components/liveReports/LiveReportsScreen.js:89-93, common/screens/PaginatedFlatList.js:37-48",
        calls: [
            { service: "getStudentLiveReports", routeParams: "{ id: élève }", urlParams: "{ page: 1 }" },
            { service: "getStudentLiveReportsWithDiagnostic", routeParams: "{ id: élève }" },
        ],
    },
    load_more: {
        gesture: "Faire défiler jusqu'au bas de la liste : la page suivante",
        legacy: "common/screens/PaginatedFlatList.js (onEndReached)",
        calls: [{ service: "getStudentLiveReports", routeParams: "{ id: élève }", urlParams: "{ page: n + 1 }" }],
    },
    refresh: {
        gesture: "Relire la liste (tirer pour rafraîchir, « Réessayer »)",
        legacy: "common/screens/PaginatedFlatList.js:37-48",
        calls: [{ service: "getStudentLiveReports", routeParams: "{ id: élève }", urlParams: "{ page: 1 }" }],
    },
    reopen_tab: {
        gesture: "Revenir sur l'onglet : la liste se relit, le diagnostic reste celui déjà lu",
        legacy: "LiveReportsScreen.js (poussé à chaque fois) ; StudentsListItem.js:271-279 (diagnostic gardé)",
        calls: [{ service: "getStudentLiveReports", routeParams: "{ id: élève }", urlParams: "{ page: 1 }" }],
    },
    open_report: {
        gesture: "Toucher un rapport avec statistiques : le rapport complet, dans l'onglet",
        legacy: 'LiveReportsScreen.js:117-123 (`push("r", { h })`), parent/LiveReports/useLiveReportDetailsData.ts',
        calls: [
            { service: "getLiveReport", routeParams: "{ hash }", urlParams: "{ with_linked_assessments: 1 }" },
            { service: "getSchoolSubjects" },
        ],
    },
    absent_report: {
        gesture: "Toucher une absence (un rapport sans statistiques) : rien",
        legacy: "LiveReportsScreen.js:117 (`disabled={!row?.statistics}`)",
        calls: [],
    },
    retry_diagnostic: {
        gesture: "« Réessayer » sur un diagnostic qui n'a pas pu se lire",
        legacy: "StudentsListItem.js:271-279",
        calls: [{ service: "getStudentLiveReportsWithDiagnostic", routeParams: "{ id: élève }" }],
    },
} as const

/** La tranche de la liste, ce que l'onglet y lit : sa réponse, son élève, son total. */
type ListSlice = {
    success?: boolean
    error?: unknown
    routeParams?: { id?: unknown }
    data?: { meta?: { total?: number } }
}

/** Aucun rapport ouvert : ce que le view-model du rapport rendrait, sans le calculer. */
const NO_OPEN_REPORT = { report: null, error: null } as const

export type TutorStudentReportsTab = TutorStudentTab<TutorStudentReportsPageProps> & {
    /** Le nombre de rapports que la liste annonce (`meta.total`), une fois lue : le compte de l'onglet. */
    count: number | undefined
}

export function useTutorStudentReportsTab(args: TutorStudentTabArgs): TutorStudentReportsTab {
    const { studentId, visible, active } = args
    const reading = visible && active && studentId != null

    // ── La liste ── (`useLiveReportsData` lit à chaque élève qu'on lui passe : `null` hors de l'onglet, puis
    // l'élève à son ouverture — d'où une lecture par ouverture, comme l'écran poussé d'avant.)
    const list = useLiveReportsData(reading ? studentId : null)
    const listViewModel = useMemo(makeTutorStudentReportsViewModel, [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => listViewModel(state, studentId))

    // La première page de cette ouverture a-t-elle répondu ? Entre l'ouverture et le départ de la lecture
    // (l'état du réseau est lu d'abord), la tranche ne dit pas encore qu'elle attend : sans cette garde,
    // la page dirait « aucun rapport » le temps d'un rendu.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listSlice = useSelector((state: any) => state?.api?.getStudentLiveReports) as ListSlice | undefined
    /**
     * La réponse de cette ouverture : l'élève pour qui elle est venue (`open`, remis à zéro hors de
     * l'onglet), et le total que la liste annonce pour lui (le compte de l'onglet, gardé d'une ouverture
     * à l'autre).
     */
    const [answered, setAnswered] = useState<{ studentId: unknown; open: boolean; total?: number }>({
        studentId: null,
        open: false,
    })
    useEffect(() => {
        if (!reading) setAnswered((was) => (was.open ? { ...was, open: false } : was))
    }, [reading])
    useEffect(() => {
        if (!reading || (!listSlice?.success && listSlice?.error == null)) return
        if (listSlice.routeParams?.id != null && String(listSlice.routeParams.id) !== String(studentId)) return
        const total = listSlice.data?.meta?.total
        setAnswered((was) => ({
            studentId,
            open: true,
            total:
                listSlice.success && typeof total === "number"
                    ? total
                    : was.studentId === studentId
                      ? was.total
                      : undefined,
        }))
    }, [reading, listSlice?.success, listSlice?.error, listSlice?.routeParams?.id, listSlice?.data, studentId])
    const awaitingFirstPage = reading && !(answered.open && answered.studentId === studentId)

    // ── Le diagnostic ──
    const diagnosticRead = useApiFetch<ApiReportWithDiagnostic[]>({
        service: liveReportService.getStudentLiveReportsWithDiagnostic,
        routeParams: { id: studentId },
        requestKey: studentId != null ? String(studentId) : null,
        enabled: studentId != null,
        fetchOnMount: false,
    })
    const diagnosticReadFor = useRef<unknown>(null)
    const { fetch: fetchDiagnosticNow } = diagnosticRead
    const dispatch = useDispatch()
    // L'erreur d'une lecture d'avant (un autre élève, ou celle qu'on réessaie) reste sur la tranche jusqu'au
    // départ de la suivante : la tranche est rendue au repos d'abord, pour ne pas la lire comme la nôtre.
    const fetchDiagnostic = useCallback(() => {
        dispatch(apiIdle(liveReportService.getStudentLiveReportsWithDiagnostic) as never)
        fetchDiagnosticNow()
    }, [dispatch, fetchDiagnosticNow])
    useEffect(() => {
        if (!reading || diagnosticReadFor.current === studentId) return
        diagnosticReadFor.current = studentId
        fetchDiagnostic()
    }, [reading, studentId, fetchDiagnostic])
    const diagnosticViewModel = useMemo(makeTutorStudentDiagnosticViewModel, [])
    const diagnostics = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => diagnosticViewModel(state, diagnosticRead.raw)
    )
    const [diagnosticIndex, setDiagnosticIndex] = useState(0)

    // ── Le rapport ouvert ──
    const [openHash, setOpenHash] = useState<string | null>(null)
    const details = useLiveReportDetailsData(openHash)
    // Le view-model du rapport (celui du parent, qui n'est pas mémoïsé) ne se lit qu'un rapport ouvert :
    // sinon une constante, et la fiche ne se redessine pas à chaque action du store.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detailsVm = useSelector((state: any) =>
        openHash != null ? liveReportDetailsViewModel(state, details.raw) : NO_OPEN_REPORT
    )

    // Un autre élève : son premier diagnostic se montre, aucun rapport n'est ouvert (son diagnostic se lit à
    // l'ouverture de l'onglet, `diagnosticReadFor`).
    useEffect(() => {
        setDiagnosticIndex(0)
        setOpenHash(null)
    }, [studentId])

    const onOpenReport = useCallback((report: UILiveSessionReport) => {
        if (report.isAbsent || !report.hash) return
        setOpenHash(report.hash)
    }, [])
    const onCloseReport = useCallback(() => setOpenHash(null), [])

    if (studentId == null) return { props: undefined, overlay: null, count: undefined }

    // Le diagnostic montré est celui de CET élève, une fois sa lecture partie.
    const diagnosticOfStudent = diagnosticReadFor.current === studentId
    const answer = diagnosticOfStudent ? diagnosticRead.error : null
    const noDiagnostic = isNoDiagnosticAnswer(answer)
    const diagnosticError = answer != null && !noDiagnostic ? answer : null
    const loadingDiagnostic =
        reading && (!diagnosticOfStudent || diagnosticRead.loading) && diagnosticError == null && !noDiagnostic
    const shownDiagnostics = diagnosticOfStudent && !noDiagnostic ? diagnostics : []

    return {
        props: {
            reports: vm.reports,
            loading: vm.loading || (awaitingFirstPage && vm.reports.length === 0 && vm.error == null),
            refreshing: vm.refreshing,
            loadingMore: vm.loadingMore,
            loadMoreError: vm.loadMoreError,
            hasMore: list.hasMore,
            totalCount: list.totalCount,
            error: vm.error,
            onRetry: list.refresh,
            onRefresh: list.refresh,
            onLoadMore: list.loadMore,
            onOpenReport,
            diagnostic: {
                diagnostics: shownDiagnostics,
                index: Math.min(diagnosticIndex, Math.max(0, shownDiagnostics.length - 1)),
                onChangeIndex: setDiagnosticIndex,
                loading: loadingDiagnostic,
                error: diagnosticError,
                onRetry: fetchDiagnostic,
            },
            openReport:
                openHash != null
                    ? {
                          report: detailsVm.report,
                          loading: details.loading,
                          error: detailsVm.error,
                          onRetry: details.retry,
                      }
                    : null,
            onCloseReport,
        },
        overlay: null,
        count: answered.studentId === studentId ? answered.total : undefined,
    }
}
